import type { StagedFieldSource, StagedValueSource } from '../../utils/stagedFieldSource';

export type LaunchDatePrecision = 'exact' | 'month' | 'quarter' | 'year';

export const EMPTY_LAUNCH_VALUE = 'Not set';

export interface DatedFieldProvenanceValue {
  source: string;
  by?: string;
  at?: string | Date;
  confidence?: number;
}

const PROVENANCE_LABEL: Record<string, { tone: StagedValueSource; label: string }> = {
  gemini_grounded: { tone: 'ai', label: 'Grounded lookup' },
  manual: { tone: 'manual', label: 'Admin edit' },
  scraped: { tone: 'scraped', label: 'Scrape' },
  official: { tone: 'official', label: 'Vendor feed' },
  ai: { tone: 'ai', label: 'AI' },
  announcement: { tone: 'official', label: 'Press announcement' },
  launch_dates: { tone: 'ai', label: 'From launch dates' },
  admin: { tone: 'manual', label: 'Admin edit' },
  sweep: { tone: 'motzu', label: 'Daily sweep' },
};

export function inferLaunchPrecision(date: string, prior?: LaunchDatePrecision): LaunchDatePrecision {
  if (prior) return prior;
  if (typeof date !== 'string') return 'exact';
  const trimmed = date.trim();
  if (/^\d{4}-\d{2}$/.test(trimmed)) return 'month';
  if (/^\d{4}$/.test(trimmed)) return 'year';
  return 'exact';
}

/** Format a stored launch / availability date for read-only display. */
export function formatLaunchDateValue(
  date: string | null | undefined,
  precision?: LaunchDatePrecision,
): string {
  if (typeof date !== 'string' || !date.trim()) return '';
  const raw = date.trim();
  const p = precision || inferLaunchPrecision(raw);

  const isoDay = raw.length >= 10 ? raw.slice(0, 10) : raw;
  const parsed = isoDay.length === 10 ? new Date(`${isoDay}T00:00:00.000Z`) : null;
  if (parsed && Number.isFinite(parsed.getTime())) {
    if (p === 'month') {
      return parsed.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
    if (p === 'quarter') {
      return `Q${Math.floor(parsed.getUTCMonth() / 3) + 1} ${parsed.getUTCFullYear()}`;
    }
    if (p === 'year') {
      return String(parsed.getUTCFullYear());
    }
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }

  if (/^\d{4}-\d{2}$/.test(raw)) {
    const [y, m] = raw.split('-').map(Number);
    if (p === 'quarter') return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
    if (p === 'year') return String(y);
    const fallback = new Date(Date.UTC(y, m - 1, 1));
    return fallback.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  if (p === 'year' && /^\d{4}$/.test(raw)) return raw;

  return raw;
}

function whenLabel(at?: string | Date): string {
  if (!at) return '';
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Chip for one field. A field stamp wins; otherwise the card-level source is the
 * fallback for rows written before per-field provenance. Empty values pass null.
 */
export function fieldProvenanceChip(
  field: DatedFieldProvenanceValue | null | undefined,
  cardSource?: string | null,
  cardConfidence?: number | null,
): StagedFieldSource | null {
  const source = field?.source || cardSource || '';
  if (!source) return null;
  const mapped = PROVENANCE_LABEL[source] || { tone: 'manual' as const, label: source.replace(/_/g, ' ') };
  const confidence = field?.confidence ?? (field ? undefined : cardConfidence ?? undefined);
  const pct = confidence != null && confidence <= 1 ? Math.round(confidence * 100) : confidence ?? undefined;
  const detail = [mapped.label, field?.by, field ? whenLabel(field.at) : ''].filter(Boolean).join(' · ');
  return {
    source: mapped.tone,
    detail,
    ...(pct != null ? { confidence: pct } : {}),
  };
}

/** @deprecated Use fieldProvenanceChip. Kept so older call sites still compile. */
export function launchDatesProvenance(source?: string, confidence?: number): StagedFieldSource | null {
  return fieldProvenanceChip(undefined, source, confidence);
}

export function lifecycleProvenance(source?: string | null): StagedFieldSource | null {
  return fieldProvenanceChip(undefined, source);
}

/** The part of a product's `launchDates` a display fallback reads. `announced` may be a legacy boolean. */
export interface LaunchDatesForLifecycle {
  announced?: string | boolean | null;
  us?: { date?: string | null } | null;
  canada?: { date?: string | null } | null;
  global?: { date?: string | null } | null;
}

export interface LifecycleFromLaunchDates {
  stage: 'announced' | 'preorder' | 'released';
  releaseDate: string | null;
  preorderOpensAt: null;
  source: 'launch_dates';
}

const PARTIAL_DATE = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;

/** Last calendar day a `YYYY`, `YYYY-MM` or `YYYY-MM-DD` value covers; null when it is none of those. */
function periodEnd(value: string | null | undefined): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  const match = text.match(PARTIAL_DATE);
  if (!match) return null;
  if (match[2] === undefined) return `${match[1]}-12-31`;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  const lastDay = new Date(Date.UTC(Number(match[1]), month, 0)).getUTCDate();
  if (match[3] === undefined) return `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`;
  const day = Number(match[3]);
  return day >= 1 && day <= lastDay ? text : null;
}

/**
 * What the lifecycle reads from launch dates alone (server `deriveLifecycleFromLaunchDates`,
 * MOT-687), for display when a product has no stored lifecycle: the global date, then the US
 * date, is the release day when it is a full `YYYY-MM-DD`; a bare year or month names no day,
 * so the product stays announced while that period is ahead and reads released once it is
 * over. Null when the dates hold no announcement and no date at all.
 */
export function lifecycleFromLaunchDates(
  launchDates: LaunchDatesForLifecycle | null | undefined,
  now: Date = new Date(),
): LifecycleFromLaunchDates | null {
  if (!launchDates) return null;
  const exact = (value: string | null | undefined): string | null =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) && periodEnd(value) ? value.trim() : null;
  const releaseDate = exact(launchDates.global?.date) ?? exact(launchDates.us?.date);
  const periods = [launchDates.global?.date, launchDates.us?.date, launchDates.canada?.date]
    .map(periodEnd)
    .filter((end): end is string => end !== null);
  const announced = Boolean(launchDates.announced);
  if (!releaseDate && !announced && periods.length === 0) return null;
  const today = now.toISOString().slice(0, 10);
  const released = releaseDate ? today >= releaseDate : periods.length > 0 && periods.every((end) => end < today);
  return { stage: released ? 'released' : 'announced', releaseDate, preorderOpensAt: null, source: 'launch_dates' };
}
